import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import React, { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { Item, OptionBinding } from 'apis/restaurant-api/merchant-admin/menu2/types';

import OptionBindingListItem from './OptionBindingListItem';

type OptionBindingsProps = {
  optionBindings: OptionBinding[];
  canDrag: boolean;
  item: Item;
  onUpdateOptionBindings: (optionBindings: OptionBinding[]) => Promise<Item>;
};

export const OptionBindings = ({ optionBindings, canDrag, item, onUpdateOptionBindings }: OptionBindingsProps) => {
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 5 } }));
  const [localOptionBindings, setLocalOptionBindings] = useState<null | OptionBinding[]>();
  const [draggingOptionBinding, setDraggingOptionBinding] = useState<null | OptionBinding>(null);
  const displayOptionBindings = useMemo(
    () => localOptionBindings || optionBindings,
    [localOptionBindings, optionBindings],
  );

  const reset = useCallback(() => {
    setLocalOptionBindings(null);
    setDraggingOptionBinding(null);
  }, []);

  const handleDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      setLocalOptionBindings(optionBindings);
      setDraggingOptionBinding(optionBindings.find(optionBinding => optionBinding.id === active.id) ?? null);
    },
    [optionBindings],
  );

  const handleDragEnd = useCallback(
    async ({ over, active }: DragEndEvent) => {
      try {
        if (!over || active?.id === over.id) {
          return;
        }
        const oldIndex = optionBindings.findIndex(option => option.id === active.id);
        const newIndex = optionBindings.findIndex(option => option.id === over.id);
        const updatedOptionBindings = arrayMove(optionBindings, oldIndex, newIndex);
        setLocalOptionBindings(updatedOptionBindings);
        await onUpdateOptionBindings(updatedOptionBindings);
      } finally {
        reset();
      }
    },
    [onUpdateOptionBindings, optionBindings, reset],
  );

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      sensors={sensors}
      collisionDetection={closestCenter}
    >
      <SortableContext
        strategy={verticalListSortingStrategy}
        items={displayOptionBindings.map(({ id }) => id)}
        disabled={!canDrag}
      >
        {displayOptionBindings.map(optionBinding => (
          <OptionBindingListItem key={optionBinding.id} item={item} optionBinding={optionBinding} />
        ))}
      </SortableContext>

      {createPortal(
        <DragOverlay
          dropAnimation={{
            duration: 300,
          }}
        >
          {draggingOptionBinding && <OptionBindingListItem item={item} optionBinding={draggingOptionBinding} />}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
};
